import { useState, useEffect } from 'react';
import {
  PlusIcon,
  SearchIcon,
  ArchiveIcon,
  EditIcon,
  CheckCircleIcon,
  LightningBoltIcon,
  CheckIcon,
} from '@nimbus-ds/icons';
import {
  Button,
  Input,
  Card,
  Tag,
  Table,
  Modal,
  Title,
  Text,
  Icon,
} from '@nimbus-ds/components';
import { CustomEventForm } from '@/components/custom-events/CustomEventForm';
import {
  customEventsService,
  CustomEventDefinition,
  CreateCustomEventRequest,
} from '@/api/customEvents';
import { useToast } from '@/hooks/use-toast';

type TagAppearance = 'primary' | 'success' | 'warning' | 'danger' | 'neutral';

export function CustomEventsPage() {
  const { toast } = useToast();
  const [customEvents, setCustomEvents] = useState<CustomEventDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'draft' | 'archived'>('active'); // Phase 4: Added 'draft'
  const [selectedEvent, setSelectedEvent] = useState<CustomEventDefinition | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [eventToArchive, setEventToArchive] = useState<CustomEventDefinition | null>(null);
  const [saving, setSaving] = useState(false);
  const [storeId, setStoreId] = useState<string | null>(null);

  // Get storeId from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem('tnx_user_info');
      if (raw) {
        const userInfo = JSON.parse(raw);
        setStoreId(userInfo?.store_id || null);
      }
    } catch {
      console.error('Failed to get store_id from localStorage');
    }
  }, []);

  useEffect(() => {
    if (storeId) {
      loadCustomEvents();
    }
  }, [filterStatus, storeId]);

  const loadCustomEvents = async () => {
    if (!storeId) return;

    setLoading(true);
    try {
      const status = filterStatus === 'all' ? undefined : filterStatus;
      const response = await customEventsService.list(storeId, status as 'active' | 'draft' | 'archived' | undefined);
      setCustomEvents(response.custom_events);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to load custom events',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (data: CreateCustomEventRequest | Partial<CustomEventDefinition>) => {
    setSaving(true);
    try {
      await customEventsService.create(data as CreateCustomEventRequest);
      toast({
        title: 'Success',
        description: 'Custom event created successfully',
      });
      setShowCreateDialog(false);
      loadCustomEvents();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to create custom event',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (data: CreateCustomEventRequest | Partial<CustomEventDefinition>) => {
    if (!selectedEvent) return;

    setSaving(true);
    try {
      await customEventsService.update(selectedEvent.event_id, data as Partial<CustomEventDefinition>);
      toast({
        title: 'Success',
        description: 'Custom event updated successfully',
      });
      setShowEditDialog(false);
      setSelectedEvent(null);
      loadCustomEvents();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to update custom event',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!eventToArchive) return;

    try {
      await customEventsService.archive(eventToArchive.event_id);
      toast({
        title: 'Success',
        description: 'Custom event archived successfully',
      });
      setEventToArchive(null);
      loadCustomEvents();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to archive custom event',
        variant: 'destructive',
      });
    }
  };

  // Phase 4: Activate draft custom event
  const handleActivate = async (eventId: string, displayName: string) => {
    try {
      await customEventsService.activate(eventId);
      toast({
        title: 'Success',
        description: `"${displayName}" is now active and tracking`,
      });
      loadCustomEvents();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to activate custom event',
        variant: 'destructive',
      });
    }
  };

  const filteredEvents = customEvents.filter((event) =>
    event.event_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.display_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getCategoryBadge = (category: string) => {
    const appearances: Record<string, TagAppearance> = {
      interaction: 'primary',
      conversion_signal: 'success',
      engagement: 'neutral',
      navigation: 'warning',
    };
    return (
      <Tag appearance={appearances[category] || 'neutral'}>
        {category}
      </Tag>
    );
  };

  const getTrackingModeBadge = (mode: 'manual' | 'auto') => {
    return mode === 'auto' ? (
      <Tag appearance="primary">Auto</Tag>
    ) : (
      <Tag appearance="neutral">Manual</Tag>
    );
  };

  const getGA4StatusIcon = (event: CustomEventDefinition) => {
    if (!event.ga4_config) return null;

    // Check if event has been validated (this would come from analytics endpoint)
    // For now, just show that GA4 is configured
    return (
      <Tag appearance="neutral">
        <Icon source={<CheckCircleIcon />} color="currentColor" />
        GA4
      </Tag>
    );
  };

  // Show loading state if storeId is not yet loaded
  if (!storeId) {
    return (
      <div className="container mx-auto py-6">
        <div className="text-center py-12">
          <Text>Loading store information...</Text>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <Title as="h1">Custom Events</Title>
          <Text>Define and track custom events for your experiments</Text>
        </div>
        <Button appearance="primary" onClick={() => setShowCreateDialog(true)}>
          <Icon source={<PlusIcon />} color="currentColor" />
          New Custom Event
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <Card.Header>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search events..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                append={<SearchIcon />}
                appendPosition="start"
              />
            </div>
            <div className="flex gap-2">
              <Button
                appearance={filterStatus === 'all' ? 'primary' : 'neutral'}
                size="small"
                onClick={() => setFilterStatus('all')}
              >
                All
              </Button>
              <Button
                appearance={filterStatus === 'active' ? 'primary' : 'neutral'}
                size="small"
                onClick={() => setFilterStatus('active')}
              >
                Active
              </Button>
              <Button
                appearance={filterStatus === 'draft' ? 'primary' : 'neutral'}
                size="small"
                onClick={() => setFilterStatus('draft')}
                className={filterStatus === 'draft' ? '' : 'text-yellow-600 border-yellow-600 hover:bg-yellow-50'}
              >
                <Icon source={<LightningBoltIcon />} color="currentColor" />
                Draft
                {filterStatus !== 'draft' && customEvents.filter(e => e.status === 'draft').length > 0 && (
                  <Tag appearance="warning">{customEvents.filter(e => e.status === 'draft').length}</Tag>
                )}
              </Button>
              <Button
                appearance={filterStatus === 'archived' ? 'primary' : 'neutral'}
                size="small"
                onClick={() => setFilterStatus('archived')}
              >
                Archived
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center py-12">
              <Text>Loading custom events...</Text>
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="text-center py-12">
              <Text>
                {searchTerm
                  ? 'No events match your search'
                  : filterStatus === 'archived'
                  ? 'No archived events'
                  : filterStatus === 'draft'
                  ? 'No draft events. Draft events are created when you use the Visual Editor during experiment setup.'
                  : 'No custom events yet. Create your first one!'}
              </Text>
            </div>
          ) : (
            <Table minWidth="720px" stickyScrollbar>
              <Table.Head>
                <Table.Row>
                  <Table.Cell as="th">Event Name</Table.Cell>
                  <Table.Cell as="th">Display Name</Table.Cell>
                  <Table.Cell as="th">Category</Table.Cell>
                  <Table.Cell as="th">Tracking</Table.Cell>
                  <Table.Cell as="th">Status</Table.Cell>
                  <Table.Cell as="th" className="text-right">Actions</Table.Cell>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {filteredEvents.map((event) => (
                  <Table.Row
                    key={event.event_id}
                    className={event.status === 'draft' ? 'bg-yellow-50 border-l-4 border-l-yellow-400' : ''}
                  >
                    <Table.Cell>
                      <div className="flex items-center gap-2">
                        <code className="text-sm bg-muted px-2 py-1 rounded">
                          {event.event_name}
                        </code>
                        {getGA4StatusIcon(event)}
                        {event.tags?.includes('visual-editor') && (
                          <Tag appearance="primary">
                            <Icon source={<LightningBoltIcon />} color="currentColor" />
                            Visual
                          </Tag>
                        )}
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <div>
                        <Text color="neutral-textHigh" fontWeight="medium">{event.display_name}</Text>
                        {event.description && (
                          <Text fontSize="caption" lineClamp={1}>
                            {event.description}
                          </Text>
                        )}
                      </div>
                    </Table.Cell>
                    <Table.Cell>{getCategoryBadge(event.category)}</Table.Cell>
                    <Table.Cell>{getTrackingModeBadge(event.tracking_config.mode)}</Table.Cell>
                    <Table.Cell>
                      <Tag appearance={event.status === 'active' ? 'success' : event.status === 'draft' ? 'warning' : 'neutral'}>
                        {event.status}
                      </Tag>
                    </Table.Cell>
                    <Table.Cell className="text-right">
                      <div className="flex justify-end gap-2">
                        {event.status === 'draft' && (
                          <Button
                            appearance="primary"
                            size="small"
                            onClick={() => handleActivate(event.event_id, event.display_name)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Icon source={<CheckIcon />} color="currentColor" />
                            Activate
                          </Button>
                        )}
                        <Button
                          appearance="transparent"
                          size="small"
                          onClick={() => {
                            setSelectedEvent(event);
                            setShowEditDialog(true);
                          }}
                        >
                          <Icon source={<EditIcon />} color="currentColor" />
                        </Button>
                        {event.status === 'active' && (
                          <Button
                            appearance="transparent"
                            size="small"
                            onClick={() => setEventToArchive(event)}
                          >
                            <Icon source={<ArchiveIcon />} color="currentColor" />
                          </Button>
                        )}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Create Dialog */}
      <Modal open={showCreateDialog} onDismiss={() => setShowCreateDialog(false)} maxWidth="768px">
        <Modal.Header title="Create Custom Event" />
        <Modal.Body>
          <Text>
            Define a new custom event to track interactions, conversions, or engagement metrics.
          </Text>
          <CustomEventForm
            storeId={storeId}
            onSave={handleCreate}
            onCancel={() => setShowCreateDialog(false)}
            isSaving={saving}
          />
        </Modal.Body>
      </Modal>

      {/* Edit Dialog */}
      <Modal open={showEditDialog} onDismiss={() => setShowEditDialog(false)} maxWidth="768px">
        <Modal.Header title="Edit Custom Event" />
        <Modal.Body>
          <Text>
            Update the configuration for this custom event.
          </Text>
          {selectedEvent && (
            <CustomEventForm
              storeId={storeId}
              existingEvent={selectedEvent}
              onSave={handleUpdate}
              onCancel={() => {
                setShowEditDialog(false);
                setSelectedEvent(null);
              }}
              isSaving={saving}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* Archive Confirmation */}
      <Modal open={!!eventToArchive} onDismiss={() => setEventToArchive(null)}>
        <Modal.Header title="Archive Custom Event?" />
        <Modal.Body>
          <Text>
            Are you sure you want to archive "{eventToArchive?.display_name}"? The event will no
            longer be tracked, but historical data will be preserved. You can restore it later if
            needed.
          </Text>
        </Modal.Body>
        <Modal.Footer>
          <Button appearance="neutral" onClick={() => setEventToArchive(null)}>Cancel</Button>
          <Button appearance="danger" onClick={handleArchive}>Archive</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
